import type {
  MobileGraphqlEndpoint,
  MobileUiConfigArtifact,
} from '@/shared/mobile-service/api/cors-mobile-api';
import { displayLabel, type ResolvedColumn } from './columns';
import { isGraphQLName, toGraphQLLiteral } from './gql-literal';
import { artifactTableView, artifactTimelineView } from './labels';
import { compileWhere, type FilterState } from './where';

/**
 * Builders for the facet rail's `{root}GroupBy` and `{root}Aggregate` queries.
 *
 * mobile-service has exposed both since the schema was introspected, but nothing
 * called them: the viewer only ever issued list queries. Value counts and a real
 * min/max time range are therefore free — no backend work, one extra round trip.
 *
 * Everything here is pure so the field-selection rules stay testable without a
 * live schema.
 */

/** Chip facets past this many make the rail a scrolling wall rather than a summary. */
const MAX_FACET_FIELDS = 8;

/** Groups requested per field. One over what we show, to detect a truncated tail. */
const FACET_GROUP_LIMIT = 13;

/** Groups actually rendered per field before the "show more" affordance. */
export const FACET_GROUPS_SHOWN = 12;

/** Fields that identify a row rather than describe it — one group each, no signal. */
function isIdentifierField(field: string): boolean {
  return field === 'id' || /Id$/.test(field);
}

export interface FacetFieldPlan {
  /** GraphQL alias for this field's `groupBy` in the batched document. */
  alias: string;
  /** Field name as it appears in the group `key` and in `where`. */
  field: string;
  /** Enum member passed to `by:`, verbatim from ui-config. */
  enumValue: string;
  label: string;
}

export interface FacetRangePlan {
  alias: string;
  field: string;
  label: string;
}

export interface FacetPlan {
  fields: FacetFieldPlan[];
  /** Datetime field driving the range brush, when the type has one. */
  range: FacetRangePlan | null;
}

/**
 * Choose which fields the rail facets on.
 *
 * Only string and boolean fields become chips. Numbers and datetimes are
 * continuous — grouping them yields one bucket per distinct value, which is a
 * histogram at best and a list of every timestamp at worst; the datetime that
 * matters becomes the range brush instead.
 *
 * Fields already visible as columns are preferred, so the rail summarizes what
 * the user is looking at rather than an arbitrary slice of the schema.
 */
export function planArtifactFacets(
  artifact: MobileUiConfigArtifact | undefined,
  visibleFields: readonly string[] = [],
): FacetPlan {
  const capabilities = artifact?.capabilities;
  const groupable = capabilities?.groupable_fields ?? [];
  const filterable = capabilities?.filterable_fields ?? {};

  const visible = new Set(visibleFields);
  const candidates = groupable.filter((g) => {
    if (!isGraphQLName(g.field) || !isGraphQLName(g.enum_value)) return false;
    if (isIdentifierField(g.field)) return false;
    const kind = filterable[g.field];
    return kind === 'String' || kind === 'Boolean';
  });

  // Stable ordering: visible columns first, then ui-config order.
  const ordered = [
    ...candidates.filter((g) => visible.has(g.field)),
    ...candidates.filter((g) => !visible.has(g.field)),
  ].slice(0, MAX_FACET_FIELDS);

  const fields: FacetFieldPlan[] = ordered.map((g, i) => ({
    alias: `f${i}`,
    field: g.field,
    enumValue: g.enum_value,
    label: artifactFieldLabel(artifact, g.field),
  }));

  return { fields, range: planRangeField(artifact) };
}

/**
 * Pick the datetime field for the range brush.
 *
 * Artifacts often carry several timestamps (event time, creation time, deletion
 * time). The one the table sorts by, or the one a timeline positions on, is the
 * one the analyst thinks of as *when it happened*; anything else is metadata.
 * The field must also be covered by the type's `min`/`max` rollups, since the
 * brush's endpoints come from the aggregate query.
 */
function planRangeField(artifact: MobileUiConfigArtifact | undefined): FacetRangePlan | null {
  const capabilities = artifact?.capabilities;
  const filterable = capabilities?.filterable_fields ?? {};
  const min = new Set(capabilities?.aggregate_fields?.min ?? []);
  const max = new Set(capabilities?.aggregate_fields?.max ?? []);

  const usable = (field: string | undefined | null): field is string =>
    Boolean(field) &&
    isGraphQLName(field as string) &&
    filterable[field as string] === 'DateTime' &&
    min.has(field as string) &&
    max.has(field as string);

  const preferred = [
    artifactTableView(artifact)?.default_sort?.field,
    artifactTimelineView(artifact)?.timestamp_field,
  ].find(usable);

  const field =
    preferred ??
    Object.keys(filterable).find((name) => usable(name) && !isIdentifierField(name));

  if (!field) return null;
  return {
    alias: 'range',
    field,
    label: artifactFieldLabel(artifact, field),
  };
}

function artifactFieldLabel(artifact: MobileUiConfigArtifact | undefined, field: string): string {
  const override = artifactTableView(artifact)?.column_overrides?.find((o) => o.field === field);
  const registry = artifact?.fields.find((f) => f.name === field)?.label;
  return displayLabel(field, { override: override?.label, registry });
}

/**
 * Filters minus one field's own constraint.
 *
 * A facet that reflected its own selection would collapse to the single value
 * the user just picked, hiding every alternative — so each facet counts against
 * every filter except its own, which is what makes the counts answer "what would
 * I get if I switched to this instead".
 */
export function whereExcluding(
  filters: FilterState,
  field: string,
  allowedFields?: ReadonlySet<string>,
): Record<string, unknown> | null {
  const rest: FilterState = {};
  for (const [name, value] of Object.entries(filters)) {
    if (name !== field) rest[name] = value;
  }
  return compileWhere(rest, allowedFields);
}

export interface BuiltFacetQuery {
  query: string;
  endpoint: MobileGraphqlEndpoint;
}

/**
 * Batch every facet into one document, one aliased root field per facet.
 *
 * Aliasing keeps this to a single request no matter how many facets are planned,
 * which matters because the alternative is a request per field on every filter
 * change.
 */
export function buildFacetQuery(
  rootQuery: string | null,
  endpoint: MobileGraphqlEndpoint,
  plan: FacetPlan,
  args: {
    mobileAcquisitionId: number;
    filters: FilterState;
    allowedFields?: ReadonlySet<string>;
  },
): BuiltFacetQuery | null {
  if (!rootQuery || !isGraphQLName(rootQuery)) return null;
  if (!plan.fields.length && !plan.range) return null;

  const selections: string[] = [];

  for (const facet of plan.fields) {
    if (!isGraphQLName(facet.field) || !isGraphQLName(facet.enumValue) || !isGraphQLName(facet.alias)) {
      continue;
    }
    const where = whereExcluding(args.filters, facet.field, args.allowedFields);
    const facetArgs = [
      `mobileAcquisitionId: ${args.mobileAcquisitionId}`,
      `by: [${facet.enumValue}]`,
      `orderByCount: DESC`,
      `limit: ${FACET_GROUP_LIMIT}`,
    ];
    if (where) facetArgs.push(`where: ${toGraphQLLiteral(where)}`);
    selections.push(
      `${facet.alias}: ${rootQuery}GroupBy(${facetArgs.join(', ')}) { key { ${facet.field} } count }`,
    );
  }

  if (plan.range && isGraphQLName(plan.range.field) && isGraphQLName(plan.range.alias)) {
    const where = whereExcluding(args.filters, plan.range.field, args.allowedFields);
    const rangeArgs = [`mobileAcquisitionId: ${args.mobileAcquisitionId}`];
    if (where) rangeArgs.push(`where: ${toGraphQLLiteral(where)}`);
    selections.push(
      `${plan.range.alias}: ${rootQuery}Aggregate(${rangeArgs.join(', ')}) { count min { ${plan.range.field} } max { ${plan.range.field} } }`,
    );
  }

  if (selections.length === 0) return null;

  return {
    query: `query ArtifactFacets {\n  ${selections.join('\n  ')}\n}`,
    endpoint,
  };
}

export interface FacetValue {
  value: string;
  label: string;
  count: number;
}

export interface FacetGroup {
  field: string;
  label: string;
  values: FacetValue[];
  /** True when the type has more distinct values than were requested. */
  truncated: boolean;
}

export interface FacetRange {
  field: string;
  label: string;
  min: string | null;
  max: string | null;
}

export interface FacetResult {
  groups: FacetGroup[];
  range: FacetRange | null;
  /** Rows matching the current filters, from the aggregate query. */
  total: number | null;
}

interface GroupRow {
  key?: Record<string, unknown> | null;
  count?: number | null;
}

interface AggregateRow {
  count?: number | null;
  min?: Record<string, unknown> | null;
  max?: Record<string, unknown> | null;
}

/** Reshape the aliased GraphQL response into the rail's view model. */
export function parseFacetResponse(
  data: Record<string, unknown>,
  plan: FacetPlan,
): FacetResult {
  const groups: FacetGroup[] = [];

  for (const facet of plan.fields) {
    const rows = (data[facet.alias] as GroupRow[] | undefined) ?? [];
    const values: FacetValue[] = [];
    for (const row of rows) {
      const raw = row.key?.[facet.field];
      // A null group is real data — rows where the field is unset — but no
      // comparator in `where.ts` can select for it, so a chip would be a filter
      // the user could apply and never clear.
      if (raw == null || raw === '') continue;
      values.push({
        value: String(raw),
        label: String(raw),
        count: row.count ?? 0,
      });
    }
    if (!values.length) continue;
    groups.push({
      field: facet.field,
      label: facet.label,
      values: values.slice(0, FACET_GROUPS_SHOWN),
      truncated: values.length > FACET_GROUPS_SHOWN,
    });
  }

  let range: FacetRange | null = null;
  let total: number | null = null;
  if (plan.range) {
    const agg = data[plan.range.alias] as AggregateRow | undefined;
    total = agg?.count ?? null;
    const min = agg?.min?.[plan.range.field];
    const max = agg?.max?.[plan.range.field];
    if (min != null || max != null) {
      range = {
        field: plan.range.field,
        label: plan.range.label,
        min: min == null ? null : String(min),
        max: max == null ? null : String(max),
      };
    }
  }

  return { groups, range, total };
}

/** Facet labels prefer the grid's column label so the rail and grid agree. */
export function applyColumnLabels(plan: FacetPlan, columns: ResolvedColumn[]): FacetPlan {
  const labels = new Map(columns.map((c) => [c.field, c.label]));
  return {
    fields: plan.fields.map((f) => ({ ...f, label: labels.get(f.field) ?? f.label })),
    range: plan.range
      ? { ...plan.range, label: labels.get(plan.range.field) ?? plan.range.label }
      : null,
  };
}
